import {
  FormControlLabel,
  Checkbox as MuiCheckbox,
  FormControl,
  FormLabel,
  RadioGroup as MuiRadioGroup,
  Radio,
  Select as MuiSelect,
  MenuItem,
} from '@mui/material';
import { useField } from 'formik';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import * as Yup from 'yup';
import PhoneInputComponent from 'react-phone-input-2';
import { useTranslation } from 'react-i18next';

import 'react-phone-input-2/lib/material.css';

import {
  Text,
  Input,
  EmailInput,
  Checkbox,
  RadioGroup,
  Select,
  PhoneInput,
  Button,
  Spacer,
} from '../../types/form-fields';
import { Loader } from '../../components';

const MOBILE_RATIO = 0.8;

const getRatioValue = (value: number, reduceSize = false) => {
  return reduceSize ? Math.ceil(value * MOBILE_RATIO) : value;
};

const getInputName = (formFieldName: string): string => formFieldName.replace('campaign.formFields', 'form');

type TextFieldProps = {
  name: string;
  reduceSize?: boolean;
  getDisplayValue?: (value: string) => string;
};

export const TextField: FC<TextFieldProps> = ({ name, reduceSize = false, getDisplayValue }) => {
  const [field] = useField<Text>(name);

  const { value, fontSize, color, fontWeight, asHTML } = field.value;

  const textContent = getDisplayValue ? getDisplayValue(value || '') : value;

  return (
    <TextContent
      style={{
        fontSize: `${getRatioValue(fontSize, reduceSize)}px`,
        color: color,
        fontWeight,
      }}
      className="generic-field-text-content"
      dangerouslySetInnerHTML={asHTML ? { __html: textContent } : undefined}
    >
      {asHTML ? null : textContent}
    </TextContent>
  );
};

type InputFieldProps = {
  name: string;
  reduceSize?: boolean;
};

export const InputField: FC<InputFieldProps> = ({ name, reduceSize = false }) => {
  const [fieldSettingsProps] = useField<Input>({ name });

  const {
    value: { placeholder, required, name: inputHTMLName },
  } = fieldSettingsProps;

  const [fieldValueProps, meta, helpers] = useField<string>({
    name: getInputName(name),
    validate: (value: string): string | undefined => {
      if (required && !value) {
        return 'Requied field';
      }
    },
  });

  const getAutoComplete = (): string => {
    switch (inputHTMLName) {
      case 'name':
        return 'name';
      case 'first_name':
        return 'given-name';
      case 'last_name':
        return 'family-name';
      default:
        return 'off';
    }
  };

  return (
    <StyledInput
      placeholder={placeholder}
      autoComplete={getAutoComplete()}
      {...fieldValueProps}
      name={getAutoComplete()}
      error={Boolean(meta.touched && meta.error)}
      onChange={(e) => {
        helpers.setValue(e.target.value);
      }}
      onBlur={(e) => {
        fieldValueProps.onBlur(e);
      }}
      reduceSize={reduceSize}
    />
  );
};

type EmailInputFieldProps = {
  name: string;
  reduceSize?: boolean;
};

const emailSchema = Yup.string().email();

export const EmailInputField: FC<EmailInputFieldProps> = ({ name, reduceSize = false }) => {
  const [fieldProps] = useField<EmailInput>({ name });

  const inputName = getInputName(name);

  const {
    value: { placeholder, required },
  } = fieldProps;

  const [fieldValueProps, meta, helpers] = useField<string>({
    name: inputName,
    validate: (value: string): string | undefined => {
      if (required && !value) {
        return 'Requied field';
      }

      if (value) {
        try {
          emailSchema.validateSync(value);
        } catch (err) {
          return 'Wrong email';
        }
      }
    },
  });

  const showError = Boolean(meta.touched && meta.error);

  return (
    <InputWrapper error={showError}>
      <StyledInput
        placeholder={placeholder}
        {...fieldValueProps}
        error={showError}
        type="email"
        autoComplete="email"
        name="email"
        onChange={(e) => {
          helpers.setValue(e.target.value);
        }}
        onBlur={(e) => {
          fieldValueProps.onBlur(e);
        }}
        reduceSize={reduceSize}
      />
    </InputWrapper>
  );
};

type CheckboxFieldProps = {
  name: string;
  reduceSize?: boolean;
};

export const CheckboxField: FC<CheckboxFieldProps> = ({ name, reduceSize = false }) => {
  const [fieldProps] = useField<Checkbox>({ name });

  const {
    value: { label, fontWeight, color, fontSize, required, labelAsHTML },
  } = fieldProps;

  const [fieldValueProps, meta, helpers] = useField<boolean>({
    name: getInputName(name),
    validate: (value: boolean): string | undefined => {
      if (required && !value) {
        return 'Requied field';
      }
    },
  });

  const showError = Boolean(meta.touched && meta.error);

  return (
    <CheckboxLabel
      disableTypography
      control={
        <MuiCheckbox
          checked={fieldValueProps.value}
          style={{ color: showError ? 'red' : color }}
          size="small"
          {...fieldValueProps}
          onChange={(event) => {
            helpers.setValue(event.target.checked);
          }}
        />
      }
      label={
        <span
          style={{
            fontWeight,
            whiteSpace: 'pre-line',
            color: showError ? 'red' : 'inherit',
            fontSize: `${getRatioValue(fontSize, reduceSize)}px`,
            marginTop: '8px',
          }}
          dangerouslySetInnerHTML={labelAsHTML ? { __html: label } : undefined}
        >
          {labelAsHTML ? null : label}
        </span>
      }
      style={{ color, marginTop: '-6px', marginBottom: '-6px' }}
      error={showError}
    />
  );
};

type RadioGroupFieldProps = {
  name: string;
  reduceSize?: boolean;
};

export const RadioGroupField: FC<RadioGroupFieldProps> = ({ name, reduceSize = false }) => {
  const [fieldProps] = useField<RadioGroup>({ name });

  const {
    value: { label, fontWeight, color, row, fontSize, labelFontSize, labelFontWeight, required },
  } = fieldProps;

  const [fieldValueProps, meta] = useField<string>({
    name: getInputName(name),
    validate: (value: string): string | undefined => {
      if (required && !value) {
        return 'Requied field';
      }
    },
  });

  const showError = Boolean(meta.touched && meta.error);

  const options = fieldProps.value.options
    .split(',')
    .map((option) => option.trim())
    .filter(Boolean);

  return (
    <RadioGroupControl style={{ marginBottom: '-6px' }} error={showError}>
      <FormLabel>
        <span
          style={{
            color: showError ? 'red' : color,
            fontWeight: labelFontWeight,
            fontSize: `${getRatioValue(labelFontSize, reduceSize)}px`,
          }}
        >
          {label}
        </span>
      </FormLabel>

      <MuiRadioGroup row={row} {...fieldValueProps}>
        {options.map((option) => (
          <FormControlLabel
            key={option}
            value={option}
            control={<Radio size="small" style={{ color: showError ? 'red' : color }} />}
            label={
              <span style={{ color, fontWeight, fontSize: `${getRatioValue(fontSize, reduceSize)}px` }}>{option}</span>
            }
          />
        ))}
      </MuiRadioGroup>
    </RadioGroupControl>
  );
};

type SelectFieldProps = {
  name: string;
  reduceSize?: boolean;
};

export const SelectField: FC<SelectFieldProps> = ({ name, reduceSize = false }) => {
  const [fieldProps] = useField<Select>({ name });

  const {
    value: { required, placeholder },
  } = fieldProps;

  const [fieldValueProps, meta] = useField<string>({
    name: getInputName(name),
    validate: (value: string): string | undefined => {
      if (required && !value) {
        return 'Requied field';
      }
    },
  });

  const showError = Boolean(meta.touched && meta.error);

  const options = fieldProps.value.options.split(',').map((option) => option.trim());

  return (
    <InputWrapper error={showError}>
      <StyledMuiSelect
        reduceSize={reduceSize}
        size="small"
        classes={{ select: 'select' }}
        {...fieldValueProps}
        displayEmpty
        renderValue={(value) => {
          if (!value) {
            return <em>{placeholder}</em>;
          }
          return value as string;
        }}
        error={showError}
      >
        <StyledMenuItem disabled>
          <em>{placeholder}</em>
        </StyledMenuItem>

        {options.map((option) => (
          <StyledMenuItem key={option} value={option}>
            {option}
          </StyledMenuItem>
        ))}
      </StyledMuiSelect>
    </InputWrapper>
  );
};

type PhoneInputFieldProps = {
  name: string;
  reduceSize?: boolean;
};

const phoneSchema = Yup.string().min(7).max(15);

type PhoneInputValue = {
  value: string;
  formattedValue: string;
  country: {
    countryCode?: string;
    dialCode?: string;
    name?: string;
  };
};

export const PhoneInputField: FC<PhoneInputFieldProps> = ({ name, reduceSize = false }) => {
  const [fieldProps] = useField<PhoneInput>({ name });

  const {
    value: { defaultCountry, required },
  } = fieldProps;

  const validate = ({ value }: PhoneInputValue): string | undefined => {
    if (required && !value) {
      return 'Requied field';
    }

    if (value) {
      try {
        phoneSchema.validateSync(value);
      } catch (err) {
        return 'Wrong phone';
      }
    }
  };

  const [fieldValueProps, meta, helpers] = useField<PhoneInputValue>({
    name: getInputName(name),
    validate,
  });

  const showError = Boolean(meta.touched && meta.error);

  const getCountries = (countriesStr?: string): string[] | undefined => {
    if (!countriesStr || countriesStr.trim() === '') {
      return undefined;
    }

    return countriesStr.split(',').map((country) => country.trim());
  };

  const onlyCountries = useMemo(() => getCountries(fieldProps.value.onlyCountries), [fieldProps.value.onlyCountries]);
  const preferredCountries = useMemo(
    () => getCountries(fieldProps.value.preferredCountries),
    [fieldProps.value.preferredCountries]
  );
  const excludeCountries = useMemo(
    () => getCountries(fieldProps.value.excludeCountries),
    [fieldProps.value.excludeCountries]
  );

  return (
    <PhoneInputComponentWrapper error={showError} reduceSize={reduceSize}>
      <PhoneInputComponent
        specialLabel=""
        country={defaultCountry.toLowerCase()}
        onlyCountries={onlyCountries}
        preferredCountries={preferredCountries}
        excludeCountries={excludeCountries}
        {...fieldValueProps}
        onChange={(value, country, e, formattedValue) => {
          helpers.setValue({ value, formattedValue, country });
        }}
        value={fieldValueProps.value.value}
      />
    </PhoneInputComponentWrapper>
  );
};

type ButtonFieldProps = {
  name: string;
  isCopyButton?: boolean;
  disabled?: boolean;
  loading?: boolean;
  onClick?: () => void;
  reduceSize?: boolean;
};

export const ButtonField: FC<ButtonFieldProps> = ({
  name,
  onClick,
  disabled,
  isCopyButton = true,
  loading,
  reduceSize = false,
}) => {
  const [fieldProps] = useField<Button>({ name });
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const [buttonWidth, setButtonWidth] = useState(0);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const {
    value: { text, textColor, bgColor, size, width },
  } = fieldProps;

  const handleClick = useCallback(
    (event) => {
      event.preventDefault();

      if (onClick) {
        onClick();
      }

      if (isCopyButton) {
        setCopied(true);
      }
    },
    [onClick, isCopyButton]
  );

  useEffect(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setButtonWidth(rect.width);
    }
  }, [setButtonWidth]);

  useEffect(() => {
    if (copied) {
      timerRef.current = setTimeout(() => {
        setCopied(false);
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [copied]);

  const loaderSize = {
    small: 1.5,
    medium: 1.65,
    large: 1.75,
  };

  const renderContent = () => {
    if (loading) {
      return <Loader bgColor={bgColor} color={textColor} size={loaderSize[size]} margin="0 auto" />;
    }

    if (copied) {
      return t('copied');
    }

    return text;
  };

  return (
    <FormButton
      size={size}
      textColor={textColor}
      bgColor={bgColor}
      width={width}
      onClick={handleClick}
      ref={buttonRef}
      style={{ minWidth: `${buttonWidth}px` }}
      disabled={disabled}
      reduceSize={reduceSize}
    >
      {renderContent()}
    </FormButton>
  );
};

type SpacerFieldProps = {
  name: string;
  isDesktop?: boolean;
};

export const SpacerField: FC<SpacerFieldProps> = ({ name, isDesktop }) => {
  const [fieldProps] = useField<Spacer>({ name });
  const { size } = fieldProps.value;

  return <SpacerContainer size={getRatioValue(size, isDesktop)} />;
};

const SpacerContainer = styled.div<{ size: number }>`
  width: 100%;
  height: ${(p) => p.size}px;
`;

const shakeErrorKeyframes = keyframes`
  0% {
    transform: translateX(0);
  }

  25% {
    transform: translateX(-2px);
  }

  75% {
    transform: translateX(2px);
  }

  100% {
    transform: translateX(0);
  }
`;

const InputWrapper = styled.div<{ error?: boolean }>`
  ${(p) =>
    p.error &&
    css`
      animation: ${shakeErrorKeyframes} 0.15s linear 0s 3;
    `};
`;

const StyledInput = styled.input<{ error?: boolean; reduceSize: boolean }>`
  width: 100%;
  outline: none;
  border-radius: 5px;
  padding: ${(p) => getRatioValue(12, p.reduceSize)}px;
  height: ${(p) => getRatioValue(40, p.reduceSize)}px;
  font-size: ${(p) => getRatioValue(16, p.reduceSize)}px;
  border: 1px solid #000;
  color: #000;
  background-color: #fff;
  box-sizing: border-box;
  transition: background-color 9999s linear;

  ${(p) =>
    p.error &&
    css`
      border-color: red;
    `};
`;

const TextContent = styled.div`
  white-space: pre-line;
  line-height: 1.2;
`;

const CheckboxLabel = styled(FormControlLabel)<{ error: boolean }>`
  align-items: flex-start;

  a,
  a:visited {
    color: inherit;
  }

  ${(p) =>
    p.error &&
    css`
      animation: ${shakeErrorKeyframes} 0.15s linear 0s 3;
    `};
`;

const RadioGroupControl = styled(FormControl)<{ error: boolean }>`
  ${(p) =>
    p.error &&
    css`
      animation: ${shakeErrorKeyframes} 0.15s linear 0s 3;
    `};
`;

const StyledMuiSelect = styled(MuiSelect)<{ error: boolean; reduceSize: boolean }>`
  width: 100%;

  .select {
    background-color: #fff;
    padding: ${(p) => `${getRatioValue(8.5, p.reduceSize)}px ${getRatioValue(14, p.reduceSize)}px`};
    font-size: ${(p) => getRatioValue(16, p.reduceSize)}px;
    line-height: ${(p) => getRatioValue(23, p.reduceSize)}px;
  }

  fieldset {
    border-color: #000 !important;
    border-width: 1px !important;
  }

  ${(p) =>
    p.error &&
    css`
      fieldset {
        border-color: red !important;
      }
    `};
`;

const StyledMenuItem = styled(MenuItem)`
  max-width: 290px;
  overflow: hidden;
`;

const PhoneInputComponentWrapper = styled.div<{ error?: boolean; reduceSize: boolean }>`
  .react-tel-input input {
    padding: ${(p) =>
      `${getRatioValue(10, p.reduceSize)}px ${getRatioValue(12, p.reduceSize)}px ${getRatioValue(
        10,
        p.reduceSize
      )}px 52px`};
    font-size: ${(p) => getRatioValue(16, p.reduceSize)}px;
    border: 1px solid !important;
    border-color: ${(p) => (p.error ? 'red !important' : '#000 !important')};
    box-shadow: none !important;
    width: 100%;

    ${(p) =>
      p.error &&
      css`
        animation: ${shakeErrorKeyframes} 0.15s linear 0s 3;
      `};
  }
`;

const FormButton = styled.button<{
  textColor: string;
  bgColor: string;
  size: 'small' | 'medium' | 'large';
  width: 'auto' | 'full';
  reduceSize?: boolean;
}>`
  border: none;
  margin: 0;
  padding: 0;
  width: ${(p) => (p.width === 'auto' ? 'auto' : '100%')};
  overflow: visible;
  background-color: ${(p) => p.bgColor};
  color: ${(p) => p.textColor};
  font: inherit;
  line-height: normal;
  appearance: none;
  outline: none;
  border-radius: 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  align-self: ${(p) => (p.width === 'auto' ? 'center' : 'stretch')};
  text-align: center;
  cursor: pointer;
  transition: background-color 0.15s linear;

  :hover {
    filter: brightness(95%);
  }

  :active {
    filter: brightness(90%);
  }

  ${(p) => {
    switch (p.size) {
      case 'small':
        return css`
          font-size: ${getRatioValue(13, p.reduceSize)}px;
          padding: ${getRatioValue(4, p.reduceSize)}px ${getRatioValue(10, p.reduceSize)}px;
        `;
      case 'medium':
        return css`
          font-size: ${getRatioValue(14, p.reduceSize)}px;
          padding: ${getRatioValue(6, p.reduceSize)}px ${getRatioValue(16, p.reduceSize)}px;
        `;
      case 'large':
        return css`
          font-size: ${getRatioValue(15, p.reduceSize)}px;
          padding: ${getRatioValue(8, p.reduceSize)}px ${getRatioValue(22, p.reduceSize)}px;
        `;
    }
  }};
`;
